/**
 * src/components/Toolbar.tsx — the toolbar band: upload `.tex`, download HTML / CSS / LaTeX,
 * copy the full HTML, clear the active editor tab.
 *
 * Source: `D:/WCS/latex2html-french-trad/src/components/Toolbar.tsx` (whole file, 103 lines).
 * Spec:   `docs/spec/06-frontend-components.md` §6 (props + button table), §6.1 (upload mechanics),
 *         §6.2 (filename derivation), §4.3–§4.4 (`ConverterApp.tsx:245-253` mounts this at the left
 *         of the toolbar row and owns all six callbacks), §15 D-4 / D-11 / D-21, §16 ranks 3/11/13/14.
 *
 * This component is **six ghost icon buttons and one hidden file input.** It holds no state, reads no
 * query, and knows nothing about the document. Every action is an opaque zero-argument callback into
 * `ConverterApp`. That deliberate ignorance is load-bearing: it is *why* several DF-22 defects are
 * structural rather than accidental, and it is reproduced exactly.
 *
 * ═══ DF-22 DEFECTS REPRODUCED HERE — DO NOT "FIX" ANY OF THEM ════════════════════════════════════
 *
 * **rank 3 — `.tex` upload assumes UTF-8 (`Toolbar.tsx:28`, spec §6.1 step 4, §15 D-4).**
 *   `reader.readAsText(file)` is called with **no encoding argument**, so the browser decodes as
 *   UTF-8. A ComTrad `.tex` saved as ISO-8859-1 / Windows-1252 — routine for older LaTeX toolchains —
 *   has every accented byte turned into U+FFFD (``). French scripture is accent-dense, so this
 *   silently corrupts exactly the material the app exists to publish. The fix (ArrayBuffer + BOM
 *   sniff + `TextDecoder('windows-1252')` choice) is NOT applied. See `handleFileChange` below.
 *
 * **rank 14 — download filenames are hard-coded, inconsistently (spec §6.2).**
 *   `onDownloadHtml` / `onDownloadCss` / `onDownloadLatex` take **no arguments**, so this component
 *   cannot influence a filename even in principle. `ConverterApp` therefore hard-codes
 *   `document.html` (`ConverterApp.tsx:138`) and `latex-styles.css` (`:149`) while the `.tex` path
 *   alone derives its name from the loaded document (`:162-167`:
 *   `loadedDocumentName || 'document.tex'`, then append `.tex` unless
 *   `baseName.toLowerCase().endsWith('.tex')`, so `MyDoc.TEX` is left uppercase and untouched, and
 *   titles containing `/`, `\`, `:` or newlines are never sanitised). Keeping these callbacks
 *   argument-free is what preserves the inconsistency. Do not widen the prop signature.
 *
 * **rank 11 — clear and upload wipe editor content with no confirmation (spec §15 D-21).**
 *   The `Trash2` button calls `onClear` straight from `onClick` — no `AlertDialog`, no undo, no
 *   dirty check. Worse on the upload path: `handleFileChange` hands the file contents to
 *   `onFileUpload` unconditionally, and `ConverterApp.handleFileUpload` classifies on
 *   `content.includes('\\begin{document}')` and then runs `setPreamble('')` (`ConverterApp.tsx:200`),
 *   so uploading a full document **silently empties a loaded preamble**. Nothing here guards it.
 *
 * **rank 13 — downloads reference KaTeX by CDN (spec §15 D-11). HALF FIXED 2026-08-30.**
 *   `generateFullHtml` still emits `<link href="https://cdn.jsdelivr.net/npm/katex@0.16.9/…">`
 *   (see `generateFullHtml` in `templates.ts`), which resolves in the preview iframe and breaks in
 *   a downloaded file opened offline. **The `/divider.svg` half of this defect is gone**: the page
 *   break is now an inline SVG (`PAGE_BREAK_ORNAMENT` in `commands-text.ts`), so it needs no asset
 *   at all — and it never worked, because that file was never in `public/` or anywhere else in the
 *   repository. KaTeX is now the only external reference a saved file carries. The toolbar still
 *   reproduces the remaining half of the defect by **not
 *   post-processing** anything: `onDownloadHtml` and `onCopyHtml` are opaque, the blob is built in
 *   `ConverterApp` from the converter's output verbatim, and no inlining, rewriting or asset
 *   embedding happens on the way out.
 *
 * **The double toast on upload (spec §6.1 "Double toast").**
 *   This file toasts `'File loaded successfully'` (`Toolbar.tsx:23`) and then
 *   `ConverterApp.handleFileUpload` toasts again — `'Full document loaded'` / `'Preamble loaded'` /
 *   `'Document loaded'` (`ConverterApp.tsx:202/207/212`). **Two toasts fire per upload.** Both call
 *   sites are `sonner` in the source, so this is NOT the approved DF-22 rank 9 deviation (that one is
 *   about two toast *systems* being mounted): it is a genuine duplicate that stays. Keep both.
 *
 * **rank 18 family — the Upload control is a `<label>`, not a button.**
 *   `Button asChild` renders its child through Radix `Slot`, so the visible control is a `<label>`
 *   (`Toolbar.tsx:37-47`). Labels are not in the tab order, so the affordance is not keyboard-
 *   reachable and, because Radix opens tooltips on focus as well as hover, keyboard users never see
 *   `Upload .tex file` either. Reproduced as-is.
 *
 * ═══ WHAT THIS FILE DELIBERATELY DOES NOT DO ════════════════════════════════════════════════════
 *  - No drag-and-drop file target anywhere in the app (spec §6.1) — only this hidden input.
 *  - No file-size limit and no extension validation beyond the advisory `accept` attribute; a 500 KB
 *    document is read whole onto the main thread.
 *  - No `isError`/loading/disabled state on any button — nothing here is ever disabled.
 *  - No keyboard shortcuts (DF-22 rank 15); no Ctrl/Cmd+S, no Ctrl/Cmd+O.
 *
 * ═══ THE ONE DEVIATION, AND IT IS COMPILE-ONLY ══════════════════════════════════════════════════
 * The source imports a seventh lucide icon, `Download` (`Toolbar.tsx:1`), and never renders it — the
 * source compiles it away because its tsconfig sets `noUnusedLocals: false`. L2HC's
 * `tsconfig.app.json` sets `noUnusedLocals: true`, so keeping the dead import would fail `tsc -b`.
 * It is dropped. Zero observable difference: an unrendered icon import is tree-shaken out of both
 * bundles, and nothing on screen, in the DOM, or in any download changes.
 */
import { Copy, FileCode, FileDown, FileText, Trash2, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { toast } from 'sonner'

/**
 * `Toolbar.tsx:6-13` — all six callbacks required, none optional, none parameterised except
 * `onFileUpload`. Not exported, matching the source. See DF-22 rank 14 above before adding an
 * argument to any of the download callbacks.
 */
interface ToolbarProps {
  onFileUpload: (content: string) => void
  onDownloadHtml: () => void
  onDownloadCss: () => void
  onDownloadLatex: () => void
  onCopyHtml: () => void
  onClear: () => void
}

export function Toolbar({
  onFileUpload,
  onDownloadHtml,
  onDownloadCss,
  onDownloadLatex,
  onCopyHtml,
  onClear,
}: ToolbarProps) {
  /**
   * `Toolbar.tsx:16-31`, step for step (spec §6.1):
   *   1. `e.target.files?.[0]` — first file only; a multi-select would drop the rest.
   *   2. `FileReader.onload` → `onFileUpload(result as string)` **then** the success toast, in that
   *      order (so `ConverterApp`'s own toast lands first — see the double-toast note above).
   *   3. `onerror` → `toast.error('Failed to read file')`. There is no `onabort` handler.
   *   4. `readAsText(file)` — **DF-22 rank 3: no encoding argument, UTF-8 assumed.**
   *   5. `e.target.value = ''` runs **synchronously, outside the `if`**, long before `onload` fires,
   *      so re-selecting the same file re-triggers `change`.
   *
   * `event.target?.result as string` is the source's cast (`:21`). `FileReader.result` is
   * `string | ArrayBuffer | null`; nothing narrows it and nothing checks for `null`, so a failed read
   * that still fires `load` would hand `null` to `ConverterApp`, where `content.includes(...)`
   * throws. Left exactly as written.
   */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (event) => {
        const content = event.target?.result as string
        // DF-22 rank 11 — handed straight through; `ConverterApp.handleFileUpload` may `setPreamble('')`.
        onFileUpload(content)
        // Spec §6.1 "Double toast" — `ConverterApp` toasts as well. Both stay.
        toast.success('File loaded successfully')
      }
      reader.onerror = () => {
        toast.error('Failed to read file')
      }
      // DF-22 rank 3 — no encoding detection. Latin-1 `.tex` files are mangled to U+FFFD.
      reader.readAsText(file)
    }
    e.target.value = ''
  }

  return (
    // `Toolbar.tsx:34` — the toolbar band itself. Spec §4.4 places it inside the row at
    // `ConverterApp.tsx:245`, left of the two save dialogs.
    <div className="flex items-center gap-1 p-2 bg-card border-b border-border">
      {/*
        1 — Upload. `Toolbar.tsx:35-50`. The `Button` is `asChild` over a `<label>`; the file input is
        absolutely positioned and transparent across it, so the click lands on the input.
        No `TooltipProvider` here: it is mounted once in `App.tsx:16` (spec §1.2).
      */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" className="relative" asChild>
            <label>
              <Upload className="h-4 w-4" />
              <input
                type="file"
                accept=".tex,.latex,.txt"
                onChange={handleFileChange}
                className="absolute inset-0 opacity-0 cursor-pointer"
              />
            </label>
          </Button>
        </TooltipTrigger>
        <TooltipContent>Upload .tex file</TooltipContent>
      </Tooltip>

      {/* separator — `Toolbar.tsx:52` */}
      <div className="w-px h-6 bg-border mx-1" />

      {/*
        2 — Download HTML. `Toolbar.tsx:54-61`.
        DF-22 rank 14: no argument, so the file is always `document.html` (`ConverterApp.tsx:138`).
        DF-22 rank 13: the blob is `generateFullHtml(html, css)` verbatim, so the CDN KaTeX link
        goes with it and breaks offline. The page-break separator no longer does — it is an inline
        SVG since 2026-08-30 and travels with the file.
      */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" onClick={onDownloadHtml}>
            <FileCode className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Download HTML</TooltipContent>
      </Tooltip>

      {/*
        3 — Download CSS. `Toolbar.tsx:63-70`.
        DF-22 rank 14: always `latex-styles.css` (`ConverterApp.tsx:149`).
        Note the icon pairing is the source's and looks transposed — `FileCode` for HTML is fine, but
        CSS gets `FileText` while LaTeX gets `FileDown`. Reproduced; see the report.
      */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" onClick={onDownloadCss}>
            <FileText className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Download CSS</TooltipContent>
      </Tooltip>

      {/*
        4 — Download LaTeX. `Toolbar.tsx:72-79`. The one export whose name IS derived
        (`ConverterApp.tsx:162-167`) — that asymmetry is DF-22 rank 14 and must survive the port.
        It also exports preamble + `\n\n` + document while Save Document stores the document tab
        only (spec §7.1) — a lossy, asymmetric round trip that stays.
      */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" onClick={onDownloadLatex}>
            <FileDown className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Download LaTeX</TooltipContent>
      </Tooltip>

      {/* 5 — Copy full HTML. `Toolbar.tsx:81-88`. Same CDN-KaTeX payload as the download. */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" onClick={onCopyHtml}>
            <Copy className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Copy full HTML</TooltipContent>
      </Tooltip>

      {/* separator — `Toolbar.tsx:90` */}
      <div className="w-px h-6 bg-border mx-1" />

      {/*
        6 — Clear editor. `Toolbar.tsx:92-99`.
        DF-22 rank 11: fires `onClear` directly. `ConverterApp.handleClear` (`:184-193`) wipes the
        ACTIVE tab — preamble or document — with no confirmation and no undo, and clears the matching
        `loadedDocumentName`/`loadedPreambleName`. Combined with DF-22 rank 1 (no unsaved-changes
        guard anywhere) this is a one-click, unrecoverable loss of unsaved translation work.
        No `AlertDialog`. No `window.confirm`. Deliberately.
      */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" onClick={onClear}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Clear editor</TooltipContent>
      </Tooltip>
    </div>
  )
}
